/**
 * In-app chat notifications (T4, MSG-022 · the notification half of
 * MSG-034): one OS notification for a message that mentions this person or
 * arrives in a direct room, for as long as Agora is open -- a tab, or
 * Electron's renderer, which implements the same Web Notification API
 * (`reminders.ts`'s own doc comment covers why one module already serves
 * both hosts; this one leans on the same fact rather than repeating it).
 *
 * Permission is never asked here. `Notification.permission` is one setting
 * per origin, not one per feature -- once `shell/AgoraShell.tsx`'s menu item
 * has asked for it (for meeting reminders, or now), `reminderPermission()`
 * reads `'granted'` for this module too, with no second prompt. Denied or
 * never asked, `showChatNotification` is just a quiet no-op, same as
 * `reminders.ts`'s own `showReminderNotification`.
 *
 * `App.tsx`'s `AgoraView` calls `watchChatNotifications` for as long as its
 * chat client exists, mirroring `counts.ts`'s own `watchChatUnread`: an
 * immediate check of every room already known, then a live subscription per
 * room, including one that only starts existing later (a brand-new direct
 * room, say). Framework-free and unit-testable with a stub `ChatClient`
 * (`notify.test.ts`).
 */
import type { ChatClient, ChatRoomSummary } from '@wtfalch/chat';
import { parseCallNotice } from './calls/callNotice.ts';
import type { IncomingCall } from './calls/ringing.ts';
import type { ChatTimelineEvent } from './chat/types.ts';
import { reminderPermission } from './reminders.ts';
import { pathForChat } from './routes.ts';

/** Whether `event`, just arrived in `room`, earns a notification: never this
 *  person's own message, never a redaction, never a muted room -- and
 *  otherwise only a mention or any message at all in a direct room, the
 *  scope MSG-022 draws (a channel message that does not mention this person
 *  stays in the unread count alone). */
export function isNotifiable(
  event: Pick<ChatTimelineEvent, 'mine' | 'mentionsMe' | 'deleted'>,
  room: Pick<ChatRoomSummary, 'isDirect' | 'muted'>,
): boolean {
  if (event.mine || event.deleted || room.muted) return false;
  return room.isDirect || event.mentionsMe;
}

/** Fires the one OS notification for a chat event that has earned one, and
 *  wires its click to focus Agora and open the room -- the Web Notification
 *  API only, the same shape as `reminders.ts`'s own
 *  `showReminderNotification`. A no-op without permission granted. */
export function showChatNotification(
  event: Pick<ChatTimelineEvent, 'id' | 'senderName' | 'body'>,
  room: Pick<ChatRoomSummary, 'id' | 'name'>,
  options: { agoraId: string; go: (path: string) => void },
): void {
  if (reminderPermission() !== 'granted') return;
  const notification = new Notification(room.name, {
    body: `${event.senderName}: ${event.body}`,
    tag: `wtfalch-agora-chat:${room.id}:${event.id}`,
  });
  notification.onclick = () => {
    window.focus();
    options.go(
      pathForChat(options.agoraId, { roomId: room.id, threadId: null, eventId: event.id }),
    );
    notification.close();
  };
}

/** The OS notice for an incoming call, same permission rule as
 *  `showChatNotification`: a no-op without permission. Clicking opens the room. */
export function showIncomingCallNotification(
  call: Pick<IncomingCall, 'roomId' | 'roomName' | 'callerName' | 'sessionId'>,
  options: { agoraId: string; go: (path: string) => void },
): void {
  if (reminderPermission() !== 'granted') return;
  const notification = new Notification(call.roomName, {
    body: `${call.callerName} is calling`,
    tag: `wtfalch-agora-call:${call.sessionId}`,
  });
  notification.onclick = () => {
    window.focus();
    options.go(
      pathForChat(options.agoraId, { roomId: call.roomId, threadId: null, eventId: null }),
    );
    notification.close();
  };
}

/**
 * Watches every room this client knows about live, firing one notification
 * per qualifying event the moment it arrives -- never for history, which is
 * what keeps a freshly opened Agora quiet about everything that piled up
 * while it was closed. "History" is anything timestamped before this
 * watcher started (`startedAt`, captured once at the top), never a blind
 * seed of whatever a room's timeline happens to hold the first time it is
 * watched: seeding would either swallow a brand-new direct room's own first
 * message (its room and that message can land in the same `onRoomsChanged`
 * report, so the message is already sitting in `timeline()` before this
 * ever looks) or, the opposite mistake, re-notify a week-old mention that
 * `paginate()` prepends into an already-watched room's timeline days later
 * (`ChatTimelineEvent.ts` does not change on a page landing, so an old
 * event and a truly new one are only told apart by that timestamp, never
 * by "was this here before"). `onTimelineChanged` fires for more than a new
 * message landing (an optimistic send settling, an older page arriving, a
 * thread reply), so this re-reads the room's own timeline on every call
 * rather than trusting the call itself; the timestamp rule keeps every one
 * of those re-reads from ever raising history, and a per-event `seen` set
 * is what keeps a re-check from ever notifying twice for the same real
 * arrival.
 *
 * `currentRoomId` and `hasFocus` decide the one further exception: the room
 * this person already has open, while the window itself is focused, never
 * notifies -- they are already looking at it. `hasFocus` defaults to
 * `document.hasFocus()`, and `now` to `Date.now`; a test supplies its own of
 * either.
 *
 * Returns the function that stops watching every room.
 */
export function watchChatNotifications(
  client: ChatClient,
  options: {
    agoraId: string;
    go: (path: string) => void;
    /** The room open in the chat section right now, or `null` off it
     *  entirely -- read fresh on every event rather than captured once,
     *  since it changes far more often than this watcher itself does. */
    currentRoomId: () => string | null;
    hasFocus?: () => boolean;
    now?: () => number;
  },
): () => void {
  let live = true;
  const hasFocus = options.hasFocus ?? (() => document.hasFocus());
  const now = options.now ?? (() => Date.now());
  // Everything with an older `ts` is history, whether it was already in a
  // room's timeline the moment this started watching it or paginated in
  // long after -- see this function's own doc comment for why that is the
  // one rule and not two.
  const startedAt = now();
  const seen = new Set<string>();
  const stopByRoom = new Map<string, () => void>();

  const check = (roomId: string) => {
    if (!live) return;
    const room = client.rooms().find((r) => r.id === roomId);
    if (!room) return;
    for (const event of client.timeline(roomId).events) {
      if (event.ts < startedAt) continue;
      const key = `${roomId}:${event.id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      if (parseCallNotice(event.body) !== null) continue; // rings instead
      if (!isNotifiable(event, room)) continue;
      if (options.currentRoomId() === roomId && hasFocus()) continue;
      showChatNotification(event, room, { agoraId: options.agoraId, go: options.go });
    }
  };

  const watch = (roomId: string) => {
    if (stopByRoom.has(roomId)) return;
    stopByRoom.set(
      roomId,
      client.onTimelineChanged(roomId, () => check(roomId)),
    );
    // A room's first check, right away -- covers a room `onRoomsChanged`
    // reports only later, whose own first message may already be sitting in
    // `timeline()` by the time this ever sees the room at all.
    check(roomId);
  };

  for (const room of client.rooms()) watch(room.id);
  const stopRooms = client.onRoomsChanged((rooms) => {
    for (const room of rooms) watch(room.id);
  });

  return () => {
    live = false;
    stopRooms();
    for (const stop of stopByRoom.values()) stop();
    stopByRoom.clear();
  };
}
